import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { X, Check, LayoutGrid, Tag, Video, Play } from 'lucide-react';

// 旧 VideoStyleSelectModal.scss を内製化（Wave 3d / T20）。
// `.modal-header` / `.modal-content` / `.modal-footer` / `.empty-state` / `.header-left`
// などの汎用クラスは Modal.scss・ProductSelectionModal.scss・ProductImageSelectionModal.scss が
// bare でグローバル定義しており、本モーダルにも一部が漏れて効いていた。
// 削除後も現状描画を保つため、その漏れ分（header の #fafafa 背景と上角丸、
// header-left の align-items:center、empty-state の gap/flex、
// footer のモバイル縦積みなど）も込みで内製化している。

export interface VideoTemplate {
  id: string;
  label: string;
  icon: string;
  description: string;
  gradient: string;
  category: string;
  prompt: string;
  /** 管理画面で登録したサンプル動画（CDN の URL）。無ければ従来どおりグラデーション＋アイコン */
  sampleVideoUrl?: string | null;
  sampleVideoPosterUrl?: string | null;
  /** 管理画面で登録したサムネイル。サンプル動画が無ければアイコンの代わりに、あれば再生前の静止画に使う */
  thumbnailUrl?: string | null;
}

export interface SelectedVideoStyle {
  id: string;
  label: string;
  prompt: string;
  category: string;
}

interface VideoStyleSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedStyle: SelectedVideoStyle | null) => void;
  currentStyleId?: string | null;
  templates: VideoTemplate[];
}

// Category config with colors
const CATEGORY_COLORS: Record<string, string> = {
  'マーケティング': '#3B82F6',
  'ブランディング': '#E1306C',
  'SNS': '#A855F7',
  '業界特化': '#22C55E',
  'EC': '#F59E0B',
};

const DEFAULT_CATEGORY_COLOR = '#6B7280';

/**
 * サンプル動画つきカードのプレビュー。
 * 一覧を開いた時点ではポスター画像だけを読み、動画はホバー中・選択中にだけ再生する
 * （preload="none" で 12 本ぶんの動画を一度に落とさない）。ポスターが無い場合だけ先頭フレームのために metadata を読む。
 */
const SampleVideoPreview: React.FC<{ src: string; poster?: string | null; playing: boolean; label: string }> = ({
  src,
  poster,
  playing,
  label,
}) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (playing) {
      // 自動再生が拒否されても（省電力モード等）ポスターが出ているので握りつぶしてよい
      video.play()?.catch(() => undefined);
    } else {
      video.pause();
    }
  }, [playing]);

  return (
    <>
      <video
        ref={ref}
        src={poster ? src : `${src}#t=0.1`}
        poster={poster || undefined}
        preload={poster ? 'none' : 'metadata'}
        muted
        loop
        playsInline
        aria-label={`${label}のサンプル動画`}
        data-testid="video-style-sample"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {!playing && (
        <span className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
          <Play size={12} />
          サンプル
        </span>
      )}
    </>
  );
};

const VideoStyleSelectModal: React.FC<VideoStyleSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentStyleId,
  templates,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<VideoTemplate | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [hoveredTemplateId, setHoveredTemplateId] = useState<string | null>(null);

  // Set initial selected template based on currentStyleId
  React.useEffect(() => {
    if (isOpen && currentStyleId) {
      const template = templates.find(t => t.id === currentStyleId);
      if (template) {
        setSelectedTemplate(template);
      }
    } else if (!isOpen) {
      setActiveCategory('all');
    }
  }, [isOpen, currentStyleId, templates]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(templates.map(t => t.category));
    return Array.from(cats);
  }, [templates]);

  // Build category config
  const categoryConfig = useMemo(() => {
    const config: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
      all: { label: 'すべて', icon: <LayoutGrid size={16} />, color: '#6B7280' },
    };

    categories.forEach(cat => {
      config[cat] = {
        label: cat,
        icon: <Tag size={16} />,
        color: CATEGORY_COLORS[cat] || DEFAULT_CATEGORY_COLOR,
      };
    });

    return config;
  }, [categories]);

  const handleTemplateSelect = useCallback((template: VideoTemplate) => {
    if (selectedTemplate?.id === template.id) {
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(template);
    }
  }, [selectedTemplate]);

  const handleConfirm = useCallback(() => {
    if (selectedTemplate) {
      onSelect({
        id: selectedTemplate.id,
        label: selectedTemplate.label,
        prompt: selectedTemplate.prompt,
        category: selectedTemplate.category,
      });
    } else {
      onSelect(null);
    }
    onClose();
  }, [selectedTemplate, onSelect, onClose]);

  const handleClear = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  const filteredTemplates = useMemo(() => {
    if (activeCategory === 'all') return templates;
    return templates.filter(t => t.category === activeCategory);
  }, [templates, activeCategory]);

  const getCategoryCount = (categorySlug: string) => {
    if (categorySlug === 'all') return templates.length;
    return templates.filter(t => t.category === categorySlug).length;
  };

  const categoryKeys = useMemo(() => {
    return ['all', ...categories];
  }, [categories]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6 max-[640px]:p-3"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-[640px]:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5">
          <div className="flex flex-col items-center gap-1">
            <h2 className="flex items-center gap-2 text-[18px] font-semibold text-gray-800">動画スタイルを選ぶ</h2>
          </div>
          <button
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar]:w-2">
          {/* Category Tabs */}
          <div className="mb-5 flex flex-wrap gap-2">
            {categoryKeys.map((categorySlug) => {
              const config = categoryConfig[categorySlug];
              if (!config) return null;
              const count = getCategoryCount(categorySlug);
              if (categorySlug !== 'all' && count === 0) return null;
              return (
                <button
                  key={categorySlug}
                  className={
                    activeCategory === categorySlug
                      ? 'active inline-flex cursor-pointer items-center gap-2 rounded-[20px] border border-[color:var(--category-color,#3b82f6)] bg-[color:var(--category-color,#3b82f6)] px-4 py-2 text-[13px] font-medium text-white transition-all duration-200 ease-[ease] [&>svg]:shrink-0'
                      : 'inline-flex cursor-pointer items-center gap-2 rounded-[20px] border border-gray-200 bg-gray-100 px-4 py-2 text-[13px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-200 [&>svg]:shrink-0'
                  }
                  onClick={() => setActiveCategory(categorySlug)}
                  style={{
                    '--category-color': config.color,
                  } as React.CSSProperties}
                >
                  {config.icon}
                  {config.label}
                  <span
                    className={
                      activeCategory === categorySlug
                        ? 'count rounded-[10px] bg-white/20 px-2 py-0.5 text-[12px] text-white'
                        : 'count rounded-[10px] bg-gray-200 px-2 py-0.5 text-[12px] text-gray-500'
                    }
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Styles Grid */}
          {filteredTemplates.length === 0 ? (
            <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400 [&>svg]:mb-4 [&>svg]:opacity-50">
              <Video size={48} />
              <p>
                {activeCategory === 'all'
                  ? 'テンプレートがありません'
                  : `${categoryConfig[activeCategory]?.label || activeCategory}のテンプレートがありません`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 max-[640px]:grid-cols-1">
              {filteredTemplates.map((template) => {
                const isSelected = selectedTemplate?.id === template.id;
                return (
                  <div
                    key={template.id}
                    className={
                      isSelected
                        ? 'selected cursor-pointer overflow-hidden rounded-xl border-2 border-violet-600 bg-gray-50 shadow-[0_0_0_3px_rgba(124,58,237,0.2)] transition-all duration-200 ease-[ease] hover:-translate-y-0.5'
                        : 'cursor-pointer overflow-hidden rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 ease-[ease] hover:-translate-y-0.5 hover:border-violet-600 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                    }
                    onClick={() => handleTemplateSelect(template)}
                    onMouseEnter={() => setHoveredTemplateId(template.id)}
                    onMouseLeave={() => setHoveredTemplateId((id) => (id === template.id ? null : id))}
                  >
                    <div
                      className="style-preview relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden"
                      style={{ background: template.gradient }}
                    >
                      {template.sampleVideoUrl ? (
                        <SampleVideoPreview
                          src={template.sampleVideoUrl}
                          poster={template.thumbnailUrl || template.sampleVideoPosterUrl}
                          playing={isSelected || hoveredTemplateId === template.id}
                          label={template.label}
                        />
                      ) : template.thumbnailUrl ? (
                        <img
                          src={template.thumbnailUrl}
                          alt={template.label}
                          loading="lazy"
                          data-testid="video-style-thumbnail"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      ) : (
                        <span className="template-icon text-[48px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">{template.icon}</span>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-violet-600/60 text-white">
                          <Check size={24} />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col p-3">
                      <h3 className="mb-1 block w-full text-[14px] font-semibold leading-[1.3] text-gray-800">{template.label}</h3>
                      <p className="mb-2 text-[12px] leading-[1.4] text-gray-500">{template.description}</p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className="inline-block whitespace-nowrap rounded px-2 py-[3px] text-[11px] font-medium text-white"
                          style={{ backgroundColor: CATEGORY_COLORS[template.category] || DEFAULT_CATEGORY_COLOR }}
                        >
                          {template.category}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[768px]:flex-col max-[640px]:items-stretch">
          {selectedTemplate && (
            <div className="flex min-w-0 flex-1 items-center gap-3 max-[640px]:mb-3">
              <span className="shrink-0 text-[13px] text-gray-500">選択中:</span>
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-medium text-gray-800">{selectedTemplate.label}</span>
              <button
                className="shrink-0 cursor-pointer rounded border border-gray-300 bg-transparent px-2.5 py-1 text-[12px] text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
                onClick={handleClear}
              >
                クリア
              </button>
            </div>
          )}
          <div className="flex shrink-0 gap-3 max-[640px]:justify-end">
            <button
              className="cursor-pointer rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800 max-[768px]:w-full max-[768px]:justify-center"
              onClick={onClose}
            >
              キャンセル
            </button>
            <button
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-violet-600 px-6 py-2.5 text-[14px] font-medium text-white transition-all duration-200 ease-[ease] hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-gray-400 max-[768px]:w-full max-[768px]:justify-center"
              onClick={handleConfirm}
            >
              {selectedTemplate ? '適用' : 'テンプレートなしで続行'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoStyleSelectModal;
